// =============================================================================
// Select Tenant Page - Scelta dell'organizzazione
// =============================================================================

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronRight, Mail, Check, X, Loader2 } from 'lucide-react';
import { useTenantStore, TenantMembership } from '../store/tenantStore';
import { useAuthStore } from '../store/authStore';
import {
  useMyInvites,
  useAcceptMyInvite,
  useDeclineMyInvite,
  refreshMyMemberships,
  MyInvite,
} from '../api/hooks';
import { useToast } from '../store/toastStore';
import { OrgAvatar } from '../components/common/OrgAvatar';
import { DeletedOrganizations } from '../components/common/DeletedOrganizations';
import { AccountFooter } from '../components/common/AccountFooter';

export function SelectTenantPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { memberships, selectTenant, activeTenantId } = useTenantStore();
  const location = useLocation();
  const isSwitching = Boolean((location.state as { switching?: boolean } | null)?.switching);
  const { logout, isAuthenticated } = useAuthStore();
  const user = useAuthStore((s) => s.user);
  const toast = useToast();

  // Inviti pendenti: accettarli aggiunge l'organizzazione alla lista qui sotto
  const { data: invites = [] } = useMyInvites(isAuthenticated);
  const acceptInvite = useAcceptMyInvite();
  const declineInvite = useDeclineMyInvite();
  const inviteBusy = acceptInvite.isPending || declineInvite.isPending;

  const handleAcceptInvite = async (invite: MyInvite) => {
    try {
      await acceptInvite.mutateAsync(invite.id);
      await refreshMyMemberships();
      toast.success(tCommon('tenant.inviteAccepted', { name: invite.tenantName }));
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(tCommon('tenant.inviteError'), typeof msg === 'string' ? msg : undefined);
    }
  };

  const handleDeclineInvite = async (invite: MyInvite) => {
    try {
      await declineInvite.mutateAsync(invite.id);
      toast.info(tCommon('tenant.inviteDeclined', { name: invite.tenantName }));
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(tCommon('tenant.inviteError'), typeof msg === 'string' ? msg : undefined);
    }
  };

  // Redirect se non autenticato
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Senza nessuna organizzazione questa pagina sarebbe una lista vuota con
  // il solo "esci": chi e' rimasto senza (rimosso dall'ultima a cui
  // apparteneva, oppure eliminata) va all'onboarding, dove puo' crearne una,
  // accettare un invito o ripristinarne una eliminata di recente.
  useEffect(() => {
    if (isAuthenticated && memberships.length === 0) {
      navigate('/onboarding', { replace: true });
    }
  }, [isAuthenticated, memberships.length, navigate]);

  // Con un'organizzazione gia' attiva questa schermata di norma rimbalza al
  // calendario. Non quando ci si arriva dallo switcher (`switching`): li' la
  // si e' aperta apposta, per cambiare organizzazione o per ripristinarne una
  // eliminata.
  useEffect(() => {
    if (activeTenantId && !isSwitching) {
      navigate('/scheduler', { replace: true });
    }
  }, [activeTenantId, isSwitching, navigate]);

  const handleSelect = (membership: TenantMembership) => {
    selectTenant(membership);
    navigate('/scheduler', { replace: true });
  };

  const handleLogout = () => {
    useTenantStore.getState().clearTenant();
    logout();
    navigate('/login', { replace: true });
  };

  const roleLabel: Record<string, string> = {
    OWNER: tCommon('roles.OWNER'),
    ADMIN: tCommon('roles.ADMIN'),
    WORKER: tCommon('roles.WORKER'),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-200">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('selectTenant.title')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('selectTenant.subtitle', { name: user?.firstName })}
          </p>
        </div>

        {/* Inviti in attesa */}
        {invites.length > 0 && (
          <div className="space-y-3 mb-6">
            <h2 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Mail className="w-4 h-4 text-amber-500" />
              {t('onboarding.pendingInvites')}
            </h2>
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="bg-white rounded-xl p-4 shadow-sm border border-amber-200 flex items-center gap-3"
              >
                <OrgAvatar branding={invite.branding} name={invite.tenantName} size="md" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {invite.tenantName}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">
                    {roleLabel[invite.role] || invite.role}
                    {invite.invitedByName &&
                      ` · ${t('onboarding.invitedBy', { name: invite.invitedByName })}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleDeclineInvite(invite)}
                    disabled={inviteBusy}
                    title={t('onboarding.declineInvite')}
                    className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 disabled:opacity-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleAcceptInvite(invite)}
                    disabled={inviteBusy}
                    className="btn-primary px-3 py-2 text-sm flex items-center gap-1.5"
                  >
                    {acceptInvite.isPending && acceptInvite.variables === invite.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {t('onboarding.acceptInvite')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Eliminate di recente: ripristinabili finche' non scade la finestra */}
        <DeletedOrganizations enabled={isAuthenticated} />

        {/* Tenant Cards */}
        <div className="space-y-3">
          {memberships.map((membership) => (
            <button
              key={membership.organizationId}
              onClick={() => handleSelect(membership)}
              className="w-full bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:border-emerald-400 hover:shadow-md transition-all flex items-center gap-4 text-left group"
            >
              <OrgAvatar
                branding={membership.branding}
                name={membership.tenantName}
                size="lg"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">
                  {membership.tenantName}
                </h3>
                <p className="text-sm text-gray-500">
                  {roleLabel[membership.role] || membership.role}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors" />
            </button>
          ))}
        </div>

        {/* Account: profilo, lingua e uscita */}
        <AccountFooter onLogout={handleLogout} />
      </div>
    </div>
  );
}
